import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useAdminCancellationRequests,
  useResolveCancellationRequest,
} from "@chotca/api-client";
import { Avatar, Button, Card, useToast } from "@chotca/ui";

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Chờ xử lý",
  APPROVED_REFUND: "Đã duyệt, hoàn lượt",
  APPROVED_NO_REFUND: "Đã duyệt, không hoàn",
  REJECTED: "Đã từ chối",
};

const REQUESTER_LABEL: Record<string, string> = {
  WORKER: "Ứng viên yêu cầu",
  EMPLOYER: "Nhà tuyển dụng yêu cầu",
  ADMIN: "Admin yêu cầu",
  SYSTEM: "Hệ thống",
};

export function CancellationsClient() {
  const [statusFilter, setStatusFilter] = useState<string>("PENDING");
  const q = useAdminCancellationRequests({ status: statusFilter, limit: 50 });
  const resolve = useResolveCancellationRequest();
  const { success: toastSuccess, error: toastError } = useToast();
  const items = q.data?.items ?? [];

  async function decide(
    requestId: string,
    resolution: "APPROVED_REFUND" | "APPROVED_NO_REFUND" | "REJECTED",
  ) {
    try {
      await resolve.mutateAsync({
        requestId,
        body: { resolution },
      });
      toastSuccess("Đã xử lý yêu cầu hủy");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Lỗi");
    }
  }

  return (
    <div className="px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">
          Yêu cầu hủy lịch PV
        </h1>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-smoke px-3 py-2 text-sm"
        >
          <option value="PENDING">Chờ xử lý</option>
          <option value="APPROVED_REFUND">Hoàn lượt</option>
          <option value="APPROVED_NO_REFUND">Không hoàn</option>
          <option value="REJECTED">Từ chối</option>
        </select>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-ash">
          Không có yêu cầu nào.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-xs text-ash">
                    Invite #{r.inviteId.slice(0, 8)} ·{" "}
                    {STATUS_LABEL[r.status] ?? r.status} ·{" "}
                    {REQUESTER_LABEL[r.requesterRole] ?? r.requesterRole}
                  </p>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-ash">
                        Nhà tuyển dụng
                      </p>
                      {r.orgId ? (
                        <Link
                          to={`/orgs/${r.orgId}`}
                          className="mt-1 inline-block text-sm font-semibold text-ink hover:underline"
                        >
                          {r.orgName ?? "Xem doanh nghiệp"}
                        </Link>
                      ) : (
                        <p className="mt-1 text-sm text-ash">Không rõ</p>
                      )}
                      {r.postingTitle ? (
                        <p className="mt-0.5 text-xs text-ash">
                          Tin: {r.postingTitle}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-ash">
                        Ứng viên
                      </p>
                      {r.workerId ? (
                        <Link
                          to={`/accounts/${r.workerId}`}
                          className="mt-1 inline-flex items-center gap-2 hover:underline"
                        >
                          <Avatar
                            size="xs"
                            name={r.workerName ?? "?"}
                            src={r.workerAvatarUrl}
                          />
                          <span className="text-sm font-semibold text-ink">
                            {r.workerName ?? "Xem ứng viên"}
                          </span>
                        </Link>
                      ) : (
                        <p className="mt-1 text-sm text-ash">Không rõ</p>
                      )}
                    </div>
                  </div>

                  <p className="mt-3 whitespace-pre-wrap text-sm text-ink">
                    {r.reason}
                  </p>
                  <p className="mt-2 text-xs text-ash">
                    {new Date(r.createdAt).toLocaleString("vi-VN")}
                  </p>
                  {r.adminNote ? (
                    <p className="mt-2 rounded bg-mist/40 p-2 text-xs text-ink">
                      Admin: {r.adminNote}
                    </p>
                  ) : null}
                </div>
                {r.status === "PENDING" ? (
                  <div className="flex shrink-0 flex-col gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => decide(r.id, "APPROVED_REFUND")}
                      disabled={resolve.isPending}
                    >
                      Hoàn lượt
                    </Button>
                    <Button
                      size="sm"
                      variant="urgent"
                      onClick={() => decide(r.id, "APPROVED_NO_REFUND")}
                      disabled={resolve.isPending}
                    >
                      Không hoàn
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => decide(r.id, "REJECTED")}
                      disabled={resolve.isPending}
                    >
                      Từ chối
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
